import React, { useEffect, useState } from "react";
import DonationModal from "./DonationModal";
import "./ReturnDonationModal.css";
import { useTranslation } from "react-i18next";
import { getDonationTotalForYear } from "../services/AnimalSplitCore";

const GOAL_USD = 550;
const GOAL_CENTS = GOAL_USD * 100;

export default function ReturnDonationModal() {
  const [showOverlay, setShowOverlay] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [totalCents, setTotalCents] = useState(null);
  const [isLoadingTotal, setIsLoadingTotal] = useState(false);
  const { t } = useTranslation();
  const campaignYear = new Date().getFullYear();

  useEffect(() => {
    try {
      console.debug("ReturnDonationModal: checking localStorage flags");
      const hasVisited = localStorage.getItem("hasVisited");
      const lastShown = localStorage.getItem("lastReturnDonateShown");
      const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
      console.debug({ hasVisited, lastShown, today });
      if (hasVisited) {
        // show once per day for returning visitors (even if donated)
        if (lastShown !== today) {
          setShowOverlay(true);
          localStorage.setItem("lastReturnDonateShown", today);
        }
      } else {
        // first time visitor: mark and don't show overlay now
        localStorage.setItem("hasVisited", "true");
      }
    } catch (e) {
      console.warn("ReturnDonationModal localStorage error", e);
    }
    // expose a dev hook to force-show the overlay from DevTools
    try {
      // non-enumerable to avoid accidental exposure
      Object.defineProperty(window, '__forceShowReturnDonate', {
        configurable: true,
        value: () => {
          console.debug('ReturnDonationModal: __forceShowReturnDonate called');
          setShowOverlay(true);
        }
      });
    } catch (e) {
      // ignore
    }

    return () => {
      try { delete window.__forceShowReturnDonate; } catch (e) {}
    };
  }, []);

  const handleClose = () => {
    setShowOverlay(false);
    try {
      const today = new Date().toISOString().slice(0, 10);
      localStorage.setItem("lastReturnDonateShown", today);
    } catch (e) {}
  };

  const openDonation = () => {
    console.debug('ReturnDonationModal: opening DonationModal (closing overlay)');
    setShowOverlay(false);
    setShowModal(true);
  };

  useEffect(() => {
    if (!showOverlay) return;

    let cancelled = false;
    (async () => {
      try {
        setIsLoadingTotal(true);
        const res = await getDonationTotalForYear(campaignYear);
        if (cancelled) return;
        setTotalCents(Number(res?.totalCents ?? 0));
      } catch (e) {
        // Non-blocking: modal should still work without the progress bar data.
        if (!cancelled) setTotalCents(null);
      } finally {
        if (!cancelled) setIsLoadingTotal(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showOverlay, campaignYear]);

  const clampPct = (pct) => Math.max(0, Math.min(100, pct));
  const hasTotal = Number.isFinite(Number(totalCents));
  const progressPct = clampPct(
    Math.round(((hasTotal ? Number(totalCents) : 0) / GOAL_CENTS) * 100),
  );

  const formatUsd = (cents) => {
    const v = (Number(cents ?? 0) || 0) / 100;
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: v % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(v);
  };

  return (
    <>
      {showOverlay && (
        <div className="return-donation-overlay" role="dialog" aria-modal="true">
          <div className="return-donation-modal as-modal as-theme">
            <div className="return-donation-content">
              <div
                className="return-donation-illustration"
                role="button"
                tabIndex={0}
                aria-label={t("return_donate.aria_open")}
                onClick={openDonation}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") openDonation();
                }}
              >
                <div className="rdm-card">
                  <div className="rdm-media">
                    <img
                      className="rdm-image"
                      src={`${process.env.PUBLIC_URL || ""}/images/image/donation.png`}
                      alt={t("return_donate.image_alt")}
                      loading="lazy"
                    />
                  </div>

                  <div className="rdm-content">
                    <div className="rdm-title">
                      {t("return_donate.title", { year: campaignYear })}
                    </div>
                    <div className="rdm-body">
                      {t("return_donate.body", { year: campaignYear, goal: GOAL_USD })}
                    </div>

                    <div className="rdm-progress">
                      <div className="rdm-progress-row">
                        <div className="rdm-progress-label">
                          {t("return_donate.progress_label", { year: campaignYear })}
                        </div>
                        <div className="rdm-progress-value">
                          {isLoadingTotal
                            ? t("return_donate.loading_total")
                            : hasTotal
                              ? `${formatUsd(totalCents)} / ${formatUsd(GOAL_CENTS)}`
                              : t("return_donate.total_unavailable")}
                        </div>
                      </div>

                      <div className="rdm-progress-track">
                        <div
                          className="rdm-progress-fill"
                          style={{ width: `${Number.isFinite(progressPct) ? progressPct : 0}%` }}
                        />
                      </div>

                      <div className="rdm-progress-meta">
                        {isLoadingTotal
                          ? "—"
                          : hasTotal
                            ? t("return_donate.progress_meta", { pct: progressPct })
                            : t("return_donate.total_unavailable")}
                      </div>
                    </div>

                    <div className="rdm-hint">
                      {t("return_donate.tap_to_donate")}
                    </div>
                  </div>
                </div>
              </div>

              <div className="return-donation-actions">
                <button
                  type="button"
                  className="btn btn-lg return-donation-reject-btn"
                  onClick={handleClose}
                >
                  💔 {t("return_donate.reject")}
                </button>
                <button
                  type="button"
                  className="btn btn-lg return-donation-donate-btn"
                  onClick={openDonation}
                >
                  ❤️ {t("general.donate")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showModal && <DonationModal onClose={() => setShowModal(false)} />}
    </>
  );
}
